import { type VariantProp, type VariantProps, cv } from "@suse-o11y/class-variance";
import {
	IconClose,
	IconErrorOutlined,
	IconInfoOutlined,
	IconSuccessOutlined,
	IconWarningOutlined,
} from "@suse-o11y/icons";
import type { Record } from "@suse-o11y/record";
import { Slot, type Slottable } from "@suse-o11y/slot";
import React from "react";

import { Button } from "~/components/button";

import styles from "./_alert.module.css";

/**
 * Check out Figma for the design of the alert:
 *
 * @link https://www.figma.com/design/YIkZms7ygvgqXYnbzDFr8Y/%23-StackState-DS?node-id=3853-54&t=UluRPSsRnaM5sATS-4
 */
const variants = cv(styles.alert, {
	variants: {
		variant: {
			info: styles.info,
			warning: styles.warning,
			error: styles.error,
			success: styles.success,
		},
	},
});

export type AlertVariant = VariantProp<typeof variants, "variant">;

const ICON_BY_VARIANT = {
	info: IconInfoOutlined,
	warning: IconWarningOutlined,
	error: IconErrorOutlined,
	success: IconSuccessOutlined,
} satisfies Record<AlertVariant>;

export interface AlertProps extends Slottable, VariantProps<typeof variants>, React.HTMLAttributes<HTMLDivElement> {
	/**
	 * A custom icon to display in the alert. The custom icon will use the `variant` color.
	 */
	icon?: React.ReactElement;

	/**
	 * When defined, renders the close button.
	 */
	onClose?: VoidFunction;

	children: React.ReactNode;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
	({ asChild, className, icon, variant: _variant, onClose, children, ...props }, ref) => {
		const variant = _variant ?? "info";
		const Content = asChild ? Slot : "div";
		const Icon = ICON_BY_VARIANT[variant];

		return (
			<div className={variants({ variant, className })} ref={ref} role="alert" {...props}>
				{icon == null ? <Icon className={styles.icon} /> : <Slot className={styles.icon}>{icon}</Slot>}

				<Content className="min-w-0 flex-1">{children}</Content>

				{onClose != null && (
					<Button className="-mt-1.5 -mr-1.5 text-grey-500 hover:text-grey-800" icon onClick={onClose} size="3xs">
						<IconClose />
					</Button>
				)}
			</div>
		);
	},
);

if (import.meta.env.DEV) {
	Alert.displayName = "Alert";
}
